// src/components/layout/GameSelector.tsx
// ============================================================
// Summit League v2 — Game Selector Dropdown
// Portaled dropdown for switching between game subdomains.
// Uses GameContext for current game, navigates via subdomain.
// ============================================================

import React, { useRef, useState, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

// ── Game Registry ──────────────────────────────────────────
// Mirrors GAME_CODE_MAP from GameContext.tsx — single source
// of truth for game metadata used by the selector UI.

export interface GameEntry {
  code: string;
  id: number;
  name: string;
  shortName: string;
}

export const AVAILABLE_GAMES: GameEntry[] = [
  { code: 'hi', id: 1, name: 'Halo Infinite', shortName: 'Infinite' },
  { code: 'h3', id: 2, name: 'Halo 3', shortName: 'H3' },
];

// ── Subdomain URL Builder ──────────────────────────────────

const SUBDOMAIN_BASE =
  process.env.REACT_APP_SUBDOMAIN_BASE || '.summitleague.gg';

const VALID_GAME_CODES = ['hi', 'h3'];

function buildGameUrl(gameCode: string): string {
  // Validate gameCode against allowlist before building subdomain URL
  if (!VALID_GAME_CODES.includes(gameCode.toLowerCase())) {
    return '/'; // Fallback to root if invalid
  }
  const protocol = window.location.protocol;
  return `${protocol}//${gameCode.toLowerCase()}${SUBDOMAIN_BASE}`;
}

// ── Dropdown Component (Portaled) ──────────────────────────

interface GameSelectorDropdownProps {
  open: boolean;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  onClose: () => void;
  currentCode: string | null;
}

function GameSelectorDropdown({
  open,
  anchorRef,
  onClose,
  currentCode,
}: GameSelectorDropdownProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  // Position relative to anchor
  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setPos({
      top: Math.round(rect.bottom + 6),
      left: Math.round(rect.left),
    });
  }, [open, anchorRef]);

  // Close on outside click or Escape
  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        anchorRef.current &&
        !anchorRef.current.contains(target)
      ) {
        onClose();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  const handleSelect = (game: GameEntry) => {
    onClose();
    if (game.code === currentCode) return;
    window.location.href = buildGameUrl(game.code);
  };

  return createPortal(
    <div
      ref={menuRef}
      className="sl-game-dropdown"
      style={{ top: `${pos.top}px`, left: `${pos.left}px` }}
      role="listbox"
      aria-label="Select game"
    >
      <div className="sl-game-dropdown-label">Switch Game</div>
      {AVAILABLE_GAMES.map((game) => {
        const isActive = game.code === currentCode;
        return (
          <button
            key={game.code}
            type="button"
            role="option"
            aria-selected={isActive}
            className={`sl-game-dropdown-item ${isActive ? 'sl-game-dropdown-item--active' : ''}`}
            onClick={() => handleSelect(game)}
          >
            <span className="sl-game-dropdown-name">{game.name}</span>
            {isActive && <span className="sl-game-dropdown-badge">Active</span>}
          </button>
        );
      })}
    </div>,
    document.body
  );
}

// ── Main GameSelector Button + Dropdown ────────────────────

interface GameSelectorProps {
  currentCode: string | null;
  currentName: string | null;
}

export default function GameSelector({ currentCode, currentName }: GameSelectorProps) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  const toggle = () => setOpen((prev) => !prev);
  const close = () => setOpen(false);

  // Display label: current game name or "Select Game"
  const label = currentName || 'Select Game';

  return (
    <>
      <div className="sl-nav-game-divider" aria-hidden="true" />

      <button
        ref={btnRef}
        type="button"
        className="sl-nav-game-btn"
        onClick={toggle}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="sl-nav-game-label">{label}</span>
        <svg
          className={`sl-nav-game-chevron ${open ? 'sl-nav-game-chevron--open' : ''}`}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M3 4.5L6 7.5L9 4.5" />
        </svg>
      </button>

      <GameSelectorDropdown
        open={open}
        anchorRef={btnRef}
        onClose={close}
        currentCode={currentCode}
      />
    </>
  );
}
